import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const PaginatedTable = ({ featureData, activeTab, favoriteZipcodes, searchTerm }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const recordsPerPage = 20;

  // Filter based on activeTab
  let filteredData = activeTab === 'favorites'
    ? featureData.filter(data => favoriteZipcodes.includes(data.zipcode))
    : featureData;

  // Filter by searchTerm (case-insensitive, across all fields)
  if (searchTerm && searchTerm.trim() !== '') {
    const lowerSearch = searchTerm.toLowerCase();
    filteredData = filteredData.filter(item =>
      Object.values(item).some(value =>
        value &&
        value.toString().toLowerCase().includes(lowerSearch)
      )
    );
  }

  // Sorting
  if (sortField) {
    filteredData = [...filteredData].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];

      // Handle numbers and strings separately
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // For string comparison (case-insensitive)
      const aStr = aVal ? aVal.toString().toLowerCase() : '';
      const bStr = bVal ? bVal.toString().toLowerCase() : '';
      if (aStr < bStr) return sortDirection === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }

  const totalRecords = filteredData.length;
  const totalPages = Math.ceil(totalRecords / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredData.slice(indexOfFirstRecord, indexOfLastRecord);

  // Reset to first page on filter, tab or sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab, sortField, sortDirection]);

  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(prev => prev + 1);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) setCurrentPage(prev => prev - 1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Helper to display sort arrow
  const renderSortArrow = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  return (
    <div>
      <table className="property-table">
        <thead>
          <tr>
            <th style={{cursor: 'pointer'}} onClick={() => handleSort('zipcode')}>Zipcode{renderSortArrow('zipcode')}</th>
            <th style={{cursor: 'pointer'}} onClick={() => handleSort('roi')}>ROI{renderSortArrow('roi')}</th>
            <th style={{cursor: 'pointer'}} onClick={() => handleSort('appr')}>Appr (%) {renderSortArrow('appr')}</th>
            <th style={{cursor: 'pointer'}} onClick={() => handleSort('score')}>Score{renderSortArrow('score')}</th>
            <th style={{cursor: 'pointer'}} onClick={() => handleSort('price')}>Price{renderSortArrow('price')}</th>
            <th style={{cursor: 'pointer'}} onClick={() => handleSort('state')}>State{renderSortArrow('state')}</th>
            <th style={{cursor: 'pointer'}} onClick={() => handleSort('city')}>City{renderSortArrow('city')}</th>
          </tr>
        </thead>
        <tbody>
          {currentRecords.map((data, index) => (
            <tr key={index}>
              <td>{data.zipcode}</td>
              <td>{data.roi}%</td>
              <td>{data.appr}%</td>
              <td>{data.score}</td>
              <td>${data.price}</td>
              <td>{data.state}</td>
              <td>{data.city}</td>
            </tr>
          ))}
          {currentRecords.length === 0 && (
            <tr>
              <td colSpan="7" style={{ textAlign: 'center', padding: '10px' }}>
                No results found.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="pagination" style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '10px' }}>
        <button onClick={goToPreviousPage} disabled={currentPage === 1}><ChevronLeft /></button>
        <span>Page {currentPage} of {totalPages}</span>
        <button onClick={goToNextPage} disabled={currentPage === totalPages}><ChevronRight /></button>
        <span>Total Records: {totalRecords}</span>
      </div>
    </div>
  );
};

export default PaginatedTable;
